/**
 * Obstacle-avoiding detour for straight-ish graph edges.
 *
 * ReactFlow's built-in paths (bezier/straight) have no obstacle awareness:
 * dagre ranks the FD camps in a column, so a tension edge between
 * non-adjacent camps draws straight through the camps in between — line AND
 * label buried under the box. For the handful of nodes on a camp map, exact
 * pathfinding is overkill: when the segment intersects a node, bow a cubic
 * bezier perpendicular past it, on whichever side needs the smaller detour.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Pt {
  x: number;
  y: number;
}

/** Liang–Barsky segment vs axis-aligned rect (rect pre-inflated by caller). */
function segmentIntersectsRect(a: Pt, b: Pt, r: Rect): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  return (
    clip(-dx, a.x - r.x) &&
    clip(dx, r.x + r.width - a.x) &&
    clip(-dy, a.y - r.y) &&
    clip(dy, r.y + r.height - a.y)
  );
}

export interface DetourPath {
  path: string;
  labelX: number;
  labelY: number;
}

/**
 * Returns a bowed cubic-bezier path when the straight segment a→b passes
 * through any obstacle (each inflated by `margin`), else null (caller keeps
 * its default path). The bow apex clears the widest obstruction by `margin`,
 * on the side requiring the smaller detour; the label anchor is the true
 * bezier midpoint, so it moves out of the box with the line.
 */
export function computeDetourPath(
  a: Pt,
  b: Pt,
  obstacles: Rect[],
  margin = 24,
): DetourPath | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  const perp = { x: -dy / len, y: dx / len };

  // Perpendicular band (relative to the a→b line) occupied by obstructions.
  let minS = Infinity;
  let maxS = -Infinity;
  let hit = false;
  for (const o of obstacles) {
    const inflated: Rect = {
      x: o.x - margin,
      y: o.y - margin,
      width: o.width + 2 * margin,
      height: o.height + 2 * margin,
    };
    if (!segmentIntersectsRect(a, b, inflated)) continue;
    hit = true;
    for (const [cx, cy] of [
      [inflated.x, inflated.y],
      [inflated.x + inflated.width, inflated.y],
      [inflated.x, inflated.y + inflated.height],
      [inflated.x + inflated.width, inflated.y + inflated.height],
    ]) {
      const s = (cx - a.x) * perp.x + (cy - a.y) * perp.y;
      minS = Math.min(minS, s);
      maxS = Math.max(maxS, s);
    }
  }
  if (!hit) return null;

  // Bow past whichever band edge is nearer to the line. The cubic's apex
  // (t=0.5, both control points offset equally) sits at 0.75 × the control
  // offset, so scale up to land the apex just past the band.
  const bow = maxS <= -minS ? maxS : minS;
  const controlOffset = bow / 0.75;

  const c1 = {
    x: a.x + dx / 3 + perp.x * controlOffset,
    y: a.y + dy / 3 + perp.y * controlOffset,
  };
  const c2 = {
    x: a.x + (2 * dx) / 3 + perp.x * controlOffset,
    y: a.y + (2 * dy) / 3 + perp.y * controlOffset,
  };
  // B(0.5) = (P0 + 3C1 + 3C2 + P3) / 8
  const labelX = (a.x + 3 * c1.x + 3 * c2.x + b.x) / 8;
  const labelY = (a.y + 3 * c1.y + 3 * c2.y + b.y) / 8;

  return {
    path: `M ${a.x},${a.y} C ${c1.x},${c1.y} ${c2.x},${c2.y} ${b.x},${b.y}`,
    labelX,
    labelY,
  };
}
